import { AlertSnackbar } from './components/AlertSnackbar'
import React, { useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import {
  AlertColor,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
  IconButton,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import { MainButton } from './components/MainButton'
import { QueryClientProvider } from 'react-query'
import { queryClient } from './query-client'
import { Box, ThemeProvider } from '@mui/system'
import { muiTheme, MUIThemeColors } from './mui-theme'
import { CancelButton } from './components/CancelButton'
import { GenericDialog } from './components/GenericDialog'
import { generateUUID } from './utils/function'
import { Flex } from './styles/flexComponent'
import { getThemeColor } from './utils'
import { GridActionButton } from './components/common/GridActionButton'
import { Padding } from './styles/paddingComponent'
import { UIProps } from './context/DialogContext'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'

const getRootEl = () => document.getElementById('dialog-root')!

export const closeAllDialog = () => {
  ReactDOM.unmountComponentAtNode(getRootEl())
}

export interface ShowDialogParams<T> {
  componentProps?: T
  scroll?: 'body' | 'paper'
  pop?: (...args: any[]) => any
  opts?: {
    fullWidth: boolean
    maxWidth: DialogProps['maxWidth']
    fullScreen?: DialogProps['fullScreen']
    draggable?: boolean
  }
  isAutoCloseDisabled?: boolean
  id?: string
  sx?: SxProps<Theme> | undefined
}

export function showMessage(msg: string, severity: AlertColor = 'success') {
  const formatted = typeof msg === 'string' ? msg : JSON.stringify(msg)

  ReactDOM.render(<Message msg={formatted} severity={severity} />, getRootEl())
}

interface MessageProps {
  msg: string
  severity?: AlertColor
}

const Message = ({ msg, severity }: MessageProps) => {
  const [open, setOpen] = useState(true)

  const onClose = () => {
    setOpen(false)
    setTimeout(() => {
      ReactDOM.unmountComponentAtNode(getRootEl()!)
    }, 100)
  }

  return <AlertSnackbar open={open} msg={msg} onClose={onClose} severity={severity} />
}

export interface ShowDialogProps<T = any> {
  pop: (...args: any[]) => T
}

export const DialogWrapper = ({
  content: Content,
  pop,
  scroll = undefined as DialogProps['scroll'],
  contentProps = {},
  opts = {},
  id = '',
  isAutoCloseDisabled = false,
  element = null,
}) => {
  const { fullWidth = true, maxWidth = 'md', PaperProps } = opts as any
  const theme = useTheme()
  const isSmall = useMediaQuery<any>(theme.breakpoints.down('sm'))
  const onPop = (data?: any) => {
    ReactDOM.unmountComponentAtNode(element ?? getRootEl())
    return pop(data)
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={muiTheme}>
        <Dialog
          // style={{ zIndex: 1000000 }} TODO: Figure out why this was here
          scroll={scroll}
          id={id}
          keepMounted={false}
          open
          onClose={!isAutoCloseDisabled ? () => onPop() : undefined}
          fullWidth={fullWidth}
          fullScreen={isSmall}
          maxWidth={maxWidth}
          PaperProps={PaperProps}
        >
          <Content {...contentProps} pop={onPop} />
        </Dialog>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export const DialogContainer = ({
  content: Content,
  pop,
  contentProps = {},
  opts = {},
  isAutoCloseDisabled = false,
  title,
  isWithLayout = false,
  sx,
}) => {
  const { maxWidth = 'md', fullWidth, fullScreen, bgColor, rootWidth, draggable } = opts as any
  const theme = useTheme()
  const isSmall = useMediaQuery<any>(theme.breakpoints.down('sm'))
  const onPop = (data?: any) => {
    return pop(data)
  }

  return (
    <GenericDialog
      open
      disableAutoClose={isAutoCloseDisabled}
      onClose={onPop}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      title={title}
      removedCloseButton={true}
      isWithLayout={isWithLayout}
      fullScreen={isSmall || fullScreen}
      maxHeight={isSmall ? '100vh' : '95vh'}
      bgColor={bgColor}
      sx={sx}
      rootWidth={rootWidth}
      draggable={draggable}
    >
      <Content {...contentProps} pop={onPop} />
    </GenericDialog>
  )
}

export const AlertDialog = ({
  title,
  text,
  acceptText = 'Accept',
  cancelText = 'Cancel',
  pop = (status: boolean) => {},
  onConfirm,
}) => {
  const acceptButton = useMemo(
    () => (
      <MainButton key="accept" variant="contained" onClick={() => (onConfirm ? onConfirm(pop) : pop(true))}>
        {acceptText}
      </MainButton>
    ),
    [acceptText, pop, onConfirm],
  )
  return (
    <>
      <GenericDialog
        open
        sx={{
          fontWeight: 'bolder',
        }}
        title={title}
        onClose={() => pop(false)}
        removedCloseButton={cancelText != 'Cancel'}
        actions={
          cancelText != 'Cancel'
            ? [
                <MainButton key={generateUUID()} onClick={() => pop(false)} variant="outlined">
                  {cancelText}
                </MainButton>,
                acceptButton,
              ]
            : [acceptButton]
        }
      >
        {text && <DialogContentText>{text}</DialogContentText>}
        <DialogActions className="mt-4"></DialogActions>
      </GenericDialog>
    </>
  )
}

export const AlertDialogCore = ({
  title,
  text,
  acceptText = 'Accept',
  cancelText = 'Cancel',
  pop = (status: boolean) => {},
  onConfirm,
  onCancel,
  UIProperties,
  isMinimal = false,
}) => {
  const theme = useTheme()
  const UIProp: UIProps = UIProperties
  return (
    <GenericDialogLayout
      renderCustomHeader={onClose =>
        isMinimal && (
          <Flex.Row justifyContent="flex-end" mt={'6px'} mr={'6px'}>
            <IconButton
              aria-label="close"
              onClick={() => {
                pop(false)
              }}
            >
              <Close />
            </IconButton>
          </Flex.Row>
        )
      }
    >
      <Flex.Row justifyContent={'center'} p={isMinimal ? '0px' : '16px'}>
        <Box
          height={isMinimal ? '40px' : '80px'}
          width={isMinimal ? '40px' : '80px'}
          sx={{ backgroundColor: UIProp.iconBGColor }}
          padding={isMinimal ? '7.5px' : '15px'}
          borderRadius={'8px'}
        >
          {UIProp?.Icon &&
            React.cloneElement(UIProp.Icon, {
              style: {
                width: isMinimal ? 24 : undefined,
                height: isMinimal ? 24 : undefined,
              },
            })}
        </Box>
      </Flex.Row>

      {title && (
        <Padding.p8>
          <Typography
            textAlign={'center'}
            variant={isMinimal ? 'h6' : 'h5'}
            color={getThemeColor(theme, MUIThemeColors.primaryDark)}
            fontWeight={600}
          >
            {title}
          </Typography>
        </Padding.p8>
      )}
      {text && (
        <Padding.p8>
          <Typography textAlign={'center'} variant={isMinimal ? 'body1' : 'subtitle1'} fontWeight={400}>
            {text}
          </Typography>
        </Padding.p8>
      )}
      <DialogActions sx={{ padding: isMinimal ? '8px 16px' : '16px 24px' }}>
        <Flex.Row flex={1} justifyContent="center" gap={2} width={'100%'}>
          <GridActionButton
            width={isMinimal ? '140px' : '180px'}
            shadowWeight={0}
            key={generateUUID()}
            onClick={() => {
              onCancel?.()
              pop(false)
            }}
            variant="outlined"
            buttonText={cancelText}
            height={isMinimal ? '44px' : '56px'}
            sx={{ border: '1px solid ' + getThemeColor(theme, MUIThemeColors.secondaryDark) }}
          />
          <GridActionButton
            width={isMinimal ? '140px' : '180px'}
            bgColor={UIProp.buttonBGColor}
            buttonTextProps={{ style: { color: UIProp.buttonTextColor } }}
            shadowWeight={0}
            key={generateUUID()}
            onClick={() => (onConfirm ? onConfirm(pop) : pop(true))}
            variant="outlined"
            buttonText={acceptText}
            height={isMinimal ? '44px' : '56px'}
          />
        </Flex.Row>
      </DialogActions>
    </GenericDialogLayout>
  )
}

export const AlertDialogContent = ({
  text,
  acceptText = 'Accept',
  cancelText = 'Cancel',
  pop = (status: boolean) => {},
  onConfirm,
}) => {
  const acceptButton = useMemo(
    () => (
      <MainButton
        key="accept"
        variant="contained"
        onClick={
          onConfirm
            ? () => {
                onConfirm()
                pop(true)
              }
            : () => pop(true)
        }
      >
        {acceptText}
      </MainButton>
    ),
    [acceptText, pop, onConfirm],
  )
  return (
    <>
      {text && <DialogContentText>{text}</DialogContentText>}
      <DialogActions className="mt-4">
        {cancelText != 'Cancel'
          ? [
              <MainButton variant="text" key="cancel" onClick={() => pop(false)}>
                {cancelText}
              </MainButton>,
              acceptButton,
            ]
          : [acceptButton]}
      </DialogActions>
    </>
  )
}

export const AlertDialogPopup = ({
  title,
  text,
  acceptText = 'Accept',
  cancelText = 'Cancel',
  pop,
  open,
  onConfirm,
}) => {
  return (
    <Dialog open={open}>
      <AlertDialog
        title={title}
        text={text}
        acceptText={acceptText}
        cancelText={cancelText}
        pop={pop}
        onConfirm={onConfirm}
      />
    </Dialog>
  )
}

export const SimpleDialog = ({ title, children, pop, actions, cancelText, open, maxWidth }) => {
  return (
    <Dialog open={open} fullWidth maxWidth={maxWidth}>
      <DialogTitle
        sx={{
          fontWeight: 'bolder',
        }}
        className="text-primary"
      >
        {title}
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
      <DialogActions className="mt-4">
        {actions}
        <CancelButton variant="text" onClick={() => pop(false)}>
          {cancelText}
        </CancelButton>
      </DialogActions>
    </Dialog>
  )
}

export function showConfirmDialog(
  title: string,
  text?: string,
  {
    acceptText = undefined,
    cancelText = undefined,
  }: { acceptText?: string | undefined; cancelText?: string | undefined } = {},
) {
  return new Promise<boolean>(resolve => {
    ReactDOM.render(
      <DialogWrapper
        pop={resolve}
        opts={{ fullWidth: false }}
        content={AlertDialog}
        contentProps={{ title, text, acceptText, cancelText }}
      />,
      getRootEl(),
    )
  })
}
export function showDeleteDialog(title: string, text?: string, { acceptText = 'Delete', cancelText = 'Cancel' } = {}) {
  return new Promise<boolean>(resolve => {
    ReactDOM.render(
      <DialogWrapper
        pop={resolve}
        opts={{ fullWidth: false }}
        content={AlertDialog}
        contentProps={{ title, text, acceptText, cancelText }}
      />,
      getRootEl(),
    )
  })
}
